import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Link as RouterLink } from "react-router-dom";
import { useGallery } from "../context/GalleryContext";
import { useLightbox } from "../hooks/useLightbox";
import SectionLabel from "../components/SectionLabel";
import ArtworkCard from "../components/ArtworkCard";
import ArtworkLightbox from "../components/ArtworkLightbox";
import { artImage } from "../utils/image";
import { tokens } from "../theme";
import { INQUIRY_EMAIL } from "../auth";

const ArrowForward = () => <Box component="span">→</Box>;

const rise = (delay: number) => ({
  animation: `tl-rise 800ms cubic-bezier(.2,.7,.2,1) both`,
  animationDelay: `${delay}ms`,
});

export default function Home() {
  const { artworks, collections } = useGallery();
  const featured = artworks.filter((a) => a.featured).slice(0, 4);
  const lb = useLightbox(artworks);
  const hero =
    artworks.find((a) => a.id === "heron") ?? featured[0] ?? artworks[0];

  return (
    <Box>
      {/* Hero */}
      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 6, md: 11 }, pb: { xs: 6, md: 10 } }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
            gap: { xs: 5, md: 8 },
            alignItems: "center",
          }}
        >
          <Box>
            <Box sx={rise(0)}>
              <SectionLabel>Original paintings</SectionLabel>
            </Box>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                mt: 3,
                fontSize: { xs: "3.1rem", sm: "4rem", md: "4.9rem" },
                color: tokens.ink,
                ...rise(80),
              }}
            >
              The coast,
              <br />
              the garden,
              <Box
                component="span"
                sx={{ fontStyle: "italic", color: tokens.accent }}
              >
                {" "}
                painted slow.
              </Box>
            </Typography>
            <Typography
              sx={{ mt: 3, maxWidth: 460, fontSize: "1.08rem", ...rise(180) }}
            >
              A small, working collection of original paintings and hand-painted
              pieces by TL. Coastal birds, marsh light, geraniums, and a few
              seasonal one-offs.
            </Typography>
            <Box
              sx={{
                mt: 4.5,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                ...rise(260),
              }}
            >
              <Button
                component={RouterLink}
                to="/works"
                variant="contained"
                color="primary"
                endIcon={<ArrowForward />}
                sx={{ py: 1.25 }}
              >
                View the works
              </Button>
              <Button
                component={RouterLink}
                to="/collections"
                variant="outlined"
                sx={{ py: 1.25 }}
              >
                Browse collections
              </Button>
            </Box>
          </Box>

          {hero && (
            <Box sx={{ ...rise(160) }}>
              <Box
                onClick={() => lb.open(hero)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && lb.open(hero)}
                sx={{ cursor: "pointer", position: "relative" }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    transform: "translate(18px,18px)",
                    border: `1px solid ${tokens.brass}`,
                    opacity: 0.5,
                    display: { xs: "none", md: "block" },
                  }}
                />
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: tokens.paper,
                    p: { xs: 1.2, md: 1.6 },
                    border: `1px solid ${tokens.line}`,
                    boxShadow: "0 24px 60px rgba(27,26,23,0.14)",
                  }}
                >
                  <Box
                    component="img"
                    src={artImage(hero)}
                    alt={hero.title}
                    sx={{ width: "100%", display: "block" }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 1.6,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.display,
                    fontStyle: "italic",
                    fontSize: "1.15rem",
                    color: tokens.ink,
                  }}
                >
                  {hero.title}
                  {hero.year ? `, ${hero.year}` : ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: tokens.taupe,
                  }}
                >
                  {hero.medium}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Container>

      {/* Featured works */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 8 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mb: 4,
          }}
        >
          <Box>
            <SectionLabel index="01">Selected works</SectionLabel>
            <Typography
              variant="h2"
              sx={{ mt: 1.5, fontSize: { xs: "2rem", md: "2.7rem" } }}
            >
              A few favorites
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/works"
            endIcon={<ArrowForward />}
            sx={{
              color: tokens.ink,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            All works
          </Button>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 4, md: 6 },
          }}
        >
          {featured.map((a) => (
            <ArtworkCard key={a.id} art={a} onOpen={lb.open} />
          ))}
        </Box>
      </Container>

      {/* Collections */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 14 } }}>
        <SectionLabel index="02">Collections</SectionLabel>
        <Box
          sx={{
            mt: 4,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 3,
          }}
        >
          {collections.map((c) => {
            const cover =
              artworks.find((a) => a.id === c.coverArtId) ??
              artworks.find((a) => a.collectionId === c.id);
            const count = artworks.filter(
              (a) => a.collectionId === c.id,
            ).length;
            return (
              <Box
                key={c.id}
                component={RouterLink}
                to={`/collections/${c.id}`}
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  "&:hover img": { transform: "scale(1.05)" },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: "4 / 5",
                    overflow: "hidden",
                    bgcolor: tokens.paper,
                    border: `1px solid ${tokens.line}`,
                  }}
                >
                  {cover && (
                    <Box
                      component="img"
                      src={artImage(cover)}
                      alt={c.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 700ms cubic-bezier(.2,.7,.2,1)",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(27,26,23,0.55), rgba(27,26,23,0) 55%)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      p: 2.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: tokens.display,
                        fontSize: "1.7rem",
                        color: tokens.bone,
                        lineHeight: 1.1,
                      }}
                    >
                      {c.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "rgba(250,247,241,0.82)",
                        mt: 0.5,
                      }}
                    >
                      {count} {count === 1 ? "piece" : "pieces"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>

      {/* Inquire band */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 14 } }}>
        <Box
          sx={{
            bgcolor: tokens.ink,
            color: tokens.bone,
            px: { xs: 4, md: 9 },
            py: { xs: 6, md: 9 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: { md: "center" },
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: tokens.display,
                fontSize: { xs: "2rem", md: "2.8rem" },
                lineHeight: 1.08,
              }}
            >
              Interested in a piece?
            </Typography>
            <Typography
              sx={{ color: "rgba(250,247,241,0.72)", mt: 1.5, maxWidth: 440 }}
            >
              Originals sell as one-of-a-kind. Reach out about availability,
              commissions, or shipping.
            </Typography>
          </Box>
          <Button
            component="a"
            href={`mailto:${INQUIRY_EMAIL}`}
            variant="contained"
            sx={{
              bgcolor: tokens.bone,
              color: tokens.ink,
              py: 1.3,
              px: 4,
              "&:hover": { bgcolor: "#fff" },
              flexShrink: 0,
            }}
          >
            Get in touch
          </Button>
        </Box>
      </Container>

      <ArtworkLightbox
        art={lb.active}
        collection={collections.find((c) => c.id === lb.active?.collectionId)}
        onClose={lb.close}
        onPrev={lb.prev}
        onNext={lb.next}
      />
    </Box>
  );
}
